/**
 * Timezone-aware itinerary helpers. Date-only values never shift with the phone zone.
 * Inputs: ISO local timestamps + IANA zones, or YYYY-MM-DD date-only strings.
 */

import { DateTime, Duration } from "luxon";

export function parseInstant(iso: string): DateTime {
  const dt = DateTime.fromISO(iso, { setZone: true });
  if (!dt.isValid) {
    throw new Error(`Invalid instant: ${iso}`);
  }
  return dt;
}

export function parseLocalInZone(localIso: string, zone: string): DateTime {
  const dt = DateTime.fromISO(localIso, { zone });
  if (!dt.isValid) {
    throw new Error(`Invalid local time ${localIso} in ${zone}`);
  }
  return dt;
}

export function parseDateOnly(date: string): DateTime {
  const dt = DateTime.fromISO(date, { zone: "utc" });
  if (!dt.isValid || date.length !== 10) {
    throw new Error(`Invalid date-only value: ${date}`);
  }
  return dt;
}

export function durationBetween(startIso: string, endIso: string): Duration {
  return parseInstant(endIso).diff(parseInstant(startIso));
}

export function formatDurationHoursMinutes(startIso: string, endIso: string): string {
  const diff = durationBetween(startIso, endIso).shiftTo("hours", "minutes");
  const hours = Math.floor(diff.hours);
  const minutes = Math.round(diff.minutes);
  return `${hours}h${String(minutes).padStart(2, "0")}`;
}

export function formatDurationPretty(startIso: string, endIso: string): string {
  const diff = durationBetween(startIso, endIso).shiftTo("hours", "minutes");
  const hours = Math.floor(diff.hours);
  const minutes = Math.round(diff.minutes);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

export function diaryDateFor(instantIso: string, diaryZone: string): string {
  return parseInstant(instantIso).setZone(diaryZone).toISODate() ?? "";
}

export function formatLocalTime(localIso: string, zone: string, pattern = "HH:mm"): string {
  return parseLocalInZone(localIso, zone).toFormat(pattern);
}

export function formatDateLabel(date: string, zone = "utc"): string {
  return DateTime.fromISO(date, { zone }).toFormat("ccc d LLL");
}

export function formatLongDate(date: string, zone = "utc"): string {
  return DateTime.fromISO(date, { zone }).toFormat("cccc d LLLL yyyy");
}

export function eachDateInclusive(startDate: string, endDate: string): string[] {
  let cursor = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  const dates: string[] = [];
  while (cursor <= end) {
    dates.push(cursor.toISODate() ?? "");
    cursor = cursor.plus({ days: 1 });
  }
  return dates;
}

export function nightsBetween(startDate: string, endDate: string): number {
  return parseDateOnly(endDate).diff(parseDateOnly(startDate), "days").days;
}

export function calendarDaysInclusive(startDate: string, endDate: string): number {
  return nightsBetween(startDate, endDate) + 1;
}

export function isOvernightArrival(departureLocalDate: string, arrivalLocalDate: string): boolean {
  return arrivalLocalDate > departureLocalDate;
}

export function compareDateOnly(a: string, b: string): number {
  return a === b ? 0 : a < b ? -1 : 1;
}

export function dateOnlyIsBefore(a: string, b: string): boolean {
  return compareDateOnly(a, b) < 0;
}

/**
 * Payment/reminder dates are civil dates, not instants. Never convert through
 * the device timezone.
 */
export function paymentDateKey(date: string): string {
  return parseDateOnly(date).toISODate() ?? date;
}

export function tripPhase(
  now: Date,
  startDate: string,
  endDate: string,
  homeZone: string,
): "before" | "during" | "after" {
  const localDate = DateTime.fromJSDate(now, { zone: homeZone }).toISODate() ?? "";
  if (localDate < startDate) return "before";
  if (localDate > endDate) return "after";
  return "during";
}

/** Follows booked transit instants, not the diary-day phase flip at midnight of day 1. */
export function transitZone(input: {
  now: Date;
  homeTimezone: string;
  diaryTimezone: string;
  firstDepartureIso?: string | null;
  lastArrivalIso?: string | null;
  airborneArrivalTimezone?: string | null;
}): string {
  const ms = input.now.getTime();
  const first = input.firstDepartureIso ? Date.parse(input.firstDepartureIso) : NaN;
  const last = input.lastArrivalIso ? Date.parse(input.lastArrivalIso) : NaN;
  if (Number.isFinite(first) && ms < first) return input.homeTimezone;
  if (Number.isFinite(last) && ms > last) return input.homeTimezone;
  if (input.airborneArrivalTimezone) return input.airborneArrivalTimezone;
  return input.diaryTimezone;
}

export function todayInZone(now: Date, zone: string): string {
  return DateTime.fromJSDate(now, { zone }).toISODate() ?? "";
}

export function injectNow(isoOrDate?: string | null): Date {
  if (!isoOrDate) return new Date();
  const dt = DateTime.fromISO(isoOrDate, { setZone: true });
  if (!dt.isValid) return new Date();
  return dt.toJSDate();
}

export function instantMs(iso: string): number {
  return parseInstant(iso).toMillis();
}

/** HH:mm from "HH:mm", "HH:mm:ss", or a local ISO timestamp. Never invents a time. */
export function clockHm(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes("T") && trimmed.length >= 16) return trimmed.slice(11, 16);
  if (trimmed.length >= 5 && !trimmed.includes("T")) return trimmed.slice(0, 5);
  return trimmed;
}

/** Clock time on a civil date. Accepts "HH:mm" or a local ISO timestamp. */
export function localTimeOnDateMs(date: string, time: string, zone: string): number | null {
  const trimmed = time.trim();
  if (!trimmed) return null;
  if (trimmed.includes("T")) {
    const dt = DateTime.fromISO(trimmed, { zone, setZone: true });
    return dt.isValid ? dt.toMillis() : null;
  }
  const hm = trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed;
  const dt = DateTime.fromISO(`${date}T${hm}`, { zone });
  return dt.isValid ? dt.toMillis() : null;
}

export function minutesAgoLabel(fetchedAt: string | null | undefined, now = new Date()): string | null {
  if (!fetchedAt) return null;
  const then = DateTime.fromISO(fetchedAt, { setZone: true });
  if (!then.isValid) return null;
  const mins = Math.max(0, Math.round(now.getTime() / 60000 - then.toMillis() / 60000));
  if (mins < 1) return "Checked just now";
  if (mins === 1) return "Checked 1 minute ago";
  if (mins < 60) return `Checked ${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  return hours === 1 ? "Checked 1 hour ago" : `Checked ${hours} hours ago`;
}
